import { useEffect, useState } from 'react';
import { match, P } from 'ts-pattern';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { cfetch, causedByAbortError, NoNetworkError } from '@confluence/network';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsEntitledExternalCollaborator } from '@confluence/user-and-group-search/entry-points/useIsEntitledExternalCollaborator.ts';

import type { UserPickerUserPrincipal } from '../../controlled/commonTypes.tsx';
// eslint-disable-next-line import/no-cycle
import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../shareAndRestrictDialogConfig.ts';
import { useUserSuggestionsMentionsExperiment } from '../../controlled/shareAndRestrictDialog/header/useUserSuggestionsMentionsExperiment.tsx';

interface UserSuggestionResponse {
	suggestedUsers: UserPickerUserPrincipal[] | null;
	loading: boolean;
	error?: Error;
}

type ResolveUserPickerPrincipalPromise = (
	value: UserPickerUserPrincipal[] | PromiseLike<UserPickerUserPrincipal[]>,
) => void;

type RejectUserPickerPrincipalPromise = (reason?: any) => void;

class UserSuggestionsCache {
	private _userSuggestions!: Promise<UserPickerUserPrincipal[]>;
	private _resolveUserSuggestions!: ResolveUserPickerPrincipalPromise;
	private _rejectUserSuggestions!: RejectUserPickerPrincipalPromise;
	private _userSuggestionsPromiseState!: 'empty' | 'pending' | 'rejected' | 'resolved';

	/** URS API Route */
	private readonly _recommendationsApi = '/gateway/api/v1/recommendations';

	/** The number of displayed suggestions below which we trigger a fetch */
	private readonly _criticallyLowDisplayResults = 3;

	/** Max number of user suggestions shown at any one time */
	private readonly _maxNumberOfDisplayResults = 3;

	/** Self-imposed limit, which if reached turns off additional fetching */
	private readonly _requestedServerResultsHardCap = 60;

	/** The amount by which we increase the requested results every time we fetch.
	 * Since the API is not paginated, we have to increase the amount of suggestions
	 * we request from the API for the user to see additional suggestions. Each additional
	 * round we increase the requested results by 10 until we reach the self-imposed hard-cap.
	 */
	private readonly _serverResultsIncreaseDelta = 10;

	/** After initial hydration we look at this variable to know whether to trigger another fetch */
	private _shouldFetchAdditionalSuggestions = false;

	/** The current amount of suggestions we request from the API. Gets increased by _serverResultsIncreaseDelta
	 * with every following fetch until the hard cap of _requestedServerResultsHardCap is reached
	 * At that point we stop allowing fetching of additional results to avoid abusing the API
	 */
	private _currentRequestedServerResults = 10;

	/** If _currentRequestedServerResults is 10, but the API only returns 5 back, then we assume
	 * that there are _noMoreServerResults, a.k.a it becomes true
	 */
	private _noMoreServerResults = false;

	/** Cancel in-flight requests when needed */
	private _abortController?: AbortController;

	private _siteId: string = '';
	private _orgId: string = '';
	private _userId: string = '';
	private _contextType: string | undefined = undefined;
	private _containerId: string | undefined = undefined;
	private _objectId: string | undefined = undefined;
	private _mentionsSessionId: string | undefined = undefined;

	constructor() {
		this.fullCacheAndStateReset();
	}

	public setContext(
		siteId: string,
		orgId: string,
		userId: string,
		contextType?: string,
		containerId?: string,
		objectId?: string,
		mentionsSessionId?: string,
	): void {
		this._siteId = siteId;
		this._orgId = orgId;
		this._userId = userId;
		this._contextType = contextType;
		this._containerId = containerId;
		this._objectId = objectId;
		this._mentionsSessionId = mentionsSessionId;
	}

	public setSiteId(siteId: string): void {
		this._siteId = siteId;
	}

	public setOrgId(orgId: string): void {
		this._orgId = orgId;
	}

	public fullCacheAndStateReset(): void {
		this._abortAndResetAbortController();
		this._resetUserSuggestionsPromiseState();
		this._resetUserSuggestionsCacheState();
	}

	public async hydrateUserSuggestionsCache(isEntitledExternalCollaboration: boolean) {
		/** Any errors here will get caught in the usePreloadUserSuggestions hook */
		await this._fetchAndCacheUserSuggestions(isEntitledExternalCollaboration);
		return this._userSuggestions;
	}

	public async getSuggestedUsers(
		currentPickedPrincipalIds: string[],
		isEntitledExternalCollaboration: boolean,
		maxDisplayResults: number = this._maxNumberOfDisplayResults,
		shouldFetchMoreResults: boolean = true,
		contextType?: string,
		siteId?: string,
		orgId?: string,
		containerId?: string,
		objectId?: string,
		mentionsSessionId?: string,
	): Promise<UserPickerUserPrincipal[]> {
		/** Any errors here will get caught in the useUserSuggestions hook */
		if (this._userSuggestionsPromiseState === 'empty' || this._shouldFetchAdditionalSuggestions) {
			if (!this._siteId && siteId) {
				this.setSiteId(siteId);
			}
			if (!this._orgId && orgId) {
				this.setOrgId(orgId);
			}
			if (!this._contextType && contextType) {
				this._contextType = contextType;
			}
			if (!this._containerId && containerId) {
				this._containerId = containerId;
			}
			if (!this._objectId && objectId) {
				this._objectId = objectId;
			}
			if (!this._mentionsSessionId && mentionsSessionId) {
				this._mentionsSessionId = mentionsSessionId;
			}
			await this._fetchAndCacheUserSuggestions(
				isEntitledExternalCollaboration,
				contextType,
				containerId,
				objectId,
				mentionsSessionId,
			);
		}

		const usersIdsExcludedFromShownSuggestions = [...currentPickedPrincipalIds, this._userId];
		const unfilteredServerResults = await this._userSuggestions;

		// Although we allow for maxDisplayResults to be any number, we only fetch 10 at a time so realistically we only display 10 max
		const userFacingDisplayResults = unfilteredServerResults
			.filter(({ id }) => !usersIdsExcludedFromShownSuggestions.includes(id))
			.slice(0, maxDisplayResults);

		this._shouldFetchAdditionalSuggestions =
			userFacingDisplayResults.length < this._criticallyLowDisplayResults &&
			this._currentRequestedServerResults <= this._requestedServerResultsHardCap &&
			this._userSuggestionsPromiseState === 'resolved' &&
			this._noMoreServerResults === false &&
			shouldFetchMoreResults;

		if (this._shouldFetchAdditionalSuggestions) {
			this._currentRequestedServerResults += this._serverResultsIncreaseDelta;
			return this.getSuggestedUsers(
				currentPickedPrincipalIds,
				isEntitledExternalCollaboration,
				maxDisplayResults,
				shouldFetchMoreResults,
				contextType,
				siteId,
				orgId,
				containerId,
				objectId,
				mentionsSessionId,
			);
		}

		return userFacingDisplayResults;
	}

	private _abortAndResetAbortController() {
		this._abortController?.abort();
		this._abortController = new AbortController();
	}

	private _resetUserSuggestionsPromiseState() {
		this._userSuggestions = new Promise((resolve, reject) => {
			this._resolveUserSuggestions = resolve;
			this._rejectUserSuggestions = reject;
		});
		this._userSuggestionsPromiseState = 'empty';
	}

	private _resetUserSuggestionsCacheState() {
		this._shouldFetchAdditionalSuggestions = false;
		this._currentRequestedServerResults = 10;
		this._noMoreServerResults = false;
	}

	private createContextPayload(
		contextType?: string,
		containerId?: string,
		objectId?: string,
		mentionsSessionId?: string,
	) {
		return match({
			contextType: contextType || this._contextType,
			containerId: containerId || this._containerId,
			objectId: objectId || this._objectId,
			mentionsSessionId: mentionsSessionId || this._mentionsSessionId,
			siteId: this._siteId,
			orgId: this._orgId,
		})
			.with(
				{
					contextType: P.string,
					containerId: P.string,
					objectId: P.string,
					mentionsSessionId: P.string,
				},
				(res) => ({
					contextType: res.contextType,
					containerId: res.containerId,
					objectId: res.objectId,
					mentionsSessionId: res.mentionsSessionId,
					principalId: 'context',
					productKey: 'confluence',
					siteId: res.siteId,
					organizationId: res.orgId,
				}),
			)
			.with(
				{
					contextType: P.string,
				},
				(res) => ({
					contextType: res.contextType,
					principalId: 'context',
					productKey: 'confluence',
					siteId: res.siteId,
					organizationId: res.orgId,
				}),
			)
			.otherwise((res) => ({
				contextType: 'share-and-restrict-dialog:user-and-group-search:suggested-share-users',
				principalId: 'context',
				productKey: 'confluence',
				siteId: res.siteId,
				organizationId: res.orgId,
			}));
	}

	private async _fetchAndCacheUserSuggestions(
		isEntitledExternalCollaboration: boolean,
		contextType?: string,
		containerId?: string,
		objectId?: string,
		mentionsSessionId?: string,
	) {
		try {
			/** No more fetching in the same dialog session if we got an error */
			if (this._userSuggestionsPromiseState === 'rejected') {
				return this._userSuggestions;
			}

			this._abortAndResetAbortController();
			this._resetUserSuggestionsPromiseState();
			this._userSuggestionsPromiseState = 'pending';

			const contextPayload: Record<string, any> = this.createContextPayload(
				contextType,
				containerId,
				objectId,
				mentionsSessionId,
			);

			const response = await cfetch(this._recommendationsApi, {
				method: 'POST',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					context: contextPayload,
					searchQuery: {
						cpusQueryHighlights: {
							query: '',
							field: '',
						},
						customQuery: '',
						customerDirectoryId: '',
						minimumAccessLevel: 'APPLICATION',

						productAccessPermissionIds: isEntitledExternalCollaboration
							? ['write', 'external-collaborator-write']
							: ['write'],

						queryString: '',
						restrictTo: {
							userIds: [],
							groupIds: [],
						},
						searchUserbase: false,
					},
					includeNonLicensedUsers: false,
					includeTeams: false,
					includeGroups: false,
					includeUsers: true,
					performSearchQueryOnly: false,
					maxNumberOfResults: this._currentRequestedServerResults,
				}),
				signal: this._abortController?.signal,
			});

			const { recommendedUsers } = await response.json();
			const formattedUsers = recommendedUsers.map(
				({
					id,
					name,
					email,
					avatarUrl,
					userType,
				}: UserPickerUserPrincipal): UserPickerUserPrincipal => ({
					type: 'user',
					id,
					name,
					email,
					avatarUrl,
					...(userType !== undefined ? { userType } : {}),
				}),
			);

			this._resolveUserSuggestions(formattedUsers);
			this._noMoreServerResults = formattedUsers.length < this._currentRequestedServerResults;
			this._userSuggestionsPromiseState = 'resolved';
		} catch (e) {
			if (e instanceof NoNetworkError && causedByAbortError(e)) {
				/** Aborted, someone's clicking too fast*/
			} else {
				/** Any errors here will get caught in the consuming methods or hooks */
				this._rejectUserSuggestions(e);
				this._userSuggestionsPromiseState = 'rejected';
			}
		} finally {
			this._shouldFetchAdditionalSuggestions = false;
		}
	}
}

export const USER_SUGGESTIONS_CACHE = new UserSuggestionsCache();

export const useUserSuggestions = ({
	currentPickedPrincipalIds,
	maxDisplayResults,
	shouldFetchMoreResults,
	contextType,
	siteId,
	orgId,
	source,
	skip = false,
	containerId,
	objectId,
	mentionsSessionId,
}: {
	currentPickedPrincipalIds: string[];
	maxDisplayResults?: number;
	shouldFetchMoreResults?: boolean;
	contextType?: string;
	siteId?: string;
	orgId?: string;
	source?: string;
	skip?: boolean;
	containerId?: string;
	objectId?: string;
	mentionsSessionId?: string;
}): UserSuggestionResponse => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isEntitledExternalCollaboration, loading: isEntitledExternalCollaborationLoading } =
		useIsEntitledExternalCollaborator();

	const [suggestedUsers, setSuggestedUsers] = useState<UserPickerUserPrincipal[] | null>(null);
	const [error, setError] = useState<Error>();
	const [loading, setLoading] = useState<boolean>(false);

	useEffect(() => {
		const _fetchUserSuggestions = async () => {
			if (skip) {
				return;
			}
			setLoading(true);
			if (!isEntitledExternalCollaborationLoading) {
				try {
					setSuggestedUsers(
						await USER_SUGGESTIONS_CACHE.getSuggestedUsers(
							currentPickedPrincipalIds,
							isEntitledExternalCollaboration,
							maxDisplayResults,
							shouldFetchMoreResults,
							contextType,
							siteId,
							orgId,
							containerId,
							objectId,
							mentionsSessionId,
						),
					);

					/** Reset error state after successful fetch */
					setError(undefined);
				} catch (e) {
					setError(e);
				} finally {
					setLoading(false);
				}
			}
		};

		void _fetchUserSuggestions();
	}, [
		currentPickedPrincipalIds,
		isEntitledExternalCollaboration,
		isEntitledExternalCollaborationLoading,
		maxDisplayResults,
		shouldFetchMoreResults,
		contextType,
		siteId,
		orgId,
		skip,
		containerId,
		objectId,
		mentionsSessionId,
	]);

	useEffect(() => {
		if (error) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: source || SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					action: 'errored',
					actionSubject: 'useUserSuggestions',
					attributes: {
						message: error?.message,
						name: error?.name,
						stack: error?.stack,
						cause: error?.cause,
					},
				},
			}).fire();
		}
	}, [error, source, createAnalyticsEvent]);

	return {
		suggestedUsers,
		loading,
		error,
	};
};

export const usePreloadUserSuggestions = ({ skip = false }: { skip?: boolean } = {}): void => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId, orgId: unsafeOrgId, userId: unsafeUserId } = useSessionData();
	const { isEntitledExternalCollaboration, loading: isEntitledExternalCollaborationLoading } =
		useIsEntitledExternalCollaborator();
	const { contentId, spaceId, contextType, mentionsSessionId } =
		useUserSuggestionsMentionsExperiment();
	const [error, setError] = useState<Error>();
	const orgId = unsafeOrgId ?? '';
	const userId = unsafeUserId ?? '';

	useEffect(() => {
		const hydrate = async () => {
			try {
				USER_SUGGESTIONS_CACHE.setContext(
					cloudId,
					orgId,
					userId,
					contextType,
					spaceId,
					contentId,
					mentionsSessionId,
				);
				await USER_SUGGESTIONS_CACHE.hydrateUserSuggestionsCache(isEntitledExternalCollaboration);
			} catch (e) {
				setError(e);
			}
		};

		if (!isEntitledExternalCollaborationLoading && !skip) {
			void hydrate();
		}
		return () => {
			USER_SUGGESTIONS_CACHE.fullCacheAndStateReset();
		};
	}, [
		cloudId,
		orgId,
		userId,
		isEntitledExternalCollaboration,
		isEntitledExternalCollaborationLoading,
		contextType,
		spaceId,
		skip,
		contentId,
		mentionsSessionId,
	]);

	useEffect(() => {
		if (error) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					action: 'errored',
					actionSubject: 'usePreloadUserSuggestions',
					attributes: {
						message: error?.message,
						name: error?.name,
						stack: error?.stack,
						cause: error?.cause,
					},
				},
			}).fire();
		}
	}, [error, createAnalyticsEvent]);
};
